import { useState, useCallback } from 'react';
import { useCards } from '../../hooks/useCards';
import type { Card, CardStats } from '../../hooks/useCards';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { PlanLimitAlert } from '../../components/feature/PlanLimitAlert';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { CardModal } from './components/CardModal';
import { CardDetailModal } from './components/CardDetailModal';
import { CardPaymentModal, CardPaymentParams } from './components/CardPaymentModal';

type ToastType = 'success' | 'error' | 'warning';
interface Toast { id: number; message: string; type: ToastType }

const TOAST_BG: Record<ToastType, string> = {
  success: 'bg-emerald-500', error: 'bg-red-500', warning: 'bg-amber-500',
};
const TOAST_ICON: Record<ToastType, string> = {
  success: 'ri-check-line', error: 'ri-error-warning-line', warning: 'ri-alert-line',
};

const BRAND_CONFIG: Record<string, { shortLabel: string; bg: string; textColor: string }> = {
  visa: { shortLabel: 'VISA', bg: '#1a1f71', textColor: '#fff' },
  mastercard: { shortLabel: 'MC', bg: 'linear-gradient(90deg,#eb001b,#f79e1b)', textColor: '#fff' },
  elo: { shortLabel: 'ELO', bg: '#ffcb05', textColor: '#111' },
  amex: { shortLabel: 'AMEX', bg: '#007bc1', textColor: '#fff' },
  hipercard: { shortLabel: 'HIPER', bg: '#b3131b', textColor: '#fff' },
  outro: { shortLabel: 'CARD', bg: '#e5e7eb', textColor: '#374151' },
};

const CARD_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  credit:   { label: 'Crédito',  icon: 'ri-bank-card-2-line' },
  debit:    { label: 'Débito',   icon: 'ri-bank-card-line' },
  multiple: { label: 'Múltiplo', icon: 'ri-swap-line' },
};

const CURRENCY_SYMBOLS: Record<string, string> = { BRL: 'R$', USD: '$', EUR: '€' };

const fmtCurrency = (val: number, currency = 'BRL') => {
  try {
    return val.toLocaleString('pt-BR', { style: 'currency', currency: currency === 'EUR' ? 'EUR' : currency === 'USD' ? 'USD' : 'BRL' });
  } catch {
    return `${CURRENCY_SYMBOLS[currency] ?? currency} ${val.toFixed(2)}`;
  }
};

type ConfirmAction =
  | { type: 'archive'; card: Card }
  | { type: 'unarchive'; card: Card }
  | { type: 'deleteBlocked'; card: Card }
  | { type: 'delete'; card: Card };

export default function CartoesPage() {
  const [showArchived, setShowArchived] = useState(false);
  const {
    cards, loading, createCard, updateCard,
    archiveCard, unarchiveCard, deleteCard, getCardStats, payCardInvoice,
  } = useCards(showArchived);

  const { checkCardLimit, isPaid } = usePlanLimits();
  const [showModal, setShowModal] = useState(false);
  const [editCard, setEditCard] = useState<Card | null>(null);
  const [detailCard, setDetailCard] = useState<Card | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    card: Card | null;
    stats: CardStats | null;
    type: 'full' | 'partial';
  }>({ open: false, card: null, stats: null, type: 'full' });

  const limitCheck = checkCardLimit();

  const addToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const handleNewCard = () => {
    if (!limitCheck.allowed) { setShowUpgrade(true); return; }
    setEditCard(null);
    setShowModal(true);
  };

  const handleEdit = (card: Card) => { setEditCard(card); setShowModal(true); };

  const handleSave = async (values: Omit<Card, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (editCard) {
      const r = await updateCard(editCard.id, values);
      if (r.error) { addToast('Erro ao atualizar cartão.', 'error'); return r; }
      addToast('Cartão atualizado!');
      return r;
    } else {
      const r = await createCard(values);
      if (r.error) { addToast('Erro ao criar cartão.', 'error'); return r; }
      addToast('Cartão criado!');
      return r;
    }
  };

  const handleArchiveConfirm = async () => {
    if (!confirmAction || confirmAction.type !== 'archive') return;
    setActionLoading(true);
    const r = await archiveCard(confirmAction.card.id);
    setActionLoading(false);
    setConfirmAction(null);
    if (r.error) addToast('Erro ao arquivar.', 'error');
    else addToast('Cartão arquivado. Histórico mantido.', 'warning');
  };

  const handleUnarchiveConfirm = async () => {
    if (!confirmAction || confirmAction.type !== 'unarchive') return;
    setActionLoading(true);
    const r = await unarchiveCard(confirmAction.card.id);
    setActionLoading(false);
    setConfirmAction(null);
    if (r.error) addToast('Erro ao reativar.', 'error');
    else addToast('Cartão reativado!');
  };

  const handleDeleteRequest = async (card: Card) => {
    setActionLoading(true);
    const result = await deleteCard(card.id);
    setActionLoading(false);
    if (result.hasTransactions) {
      setConfirmAction({ type: 'deleteBlocked', card });
    } else if (result.error) {
      addToast('Erro ao excluir.', 'error');
    } else {
      setConfirmAction(null);
      addToast('Cartão excluído.');
    }
  };

  const handlePayFull = (card: Card, stats: CardStats) => {
    setPaymentModal({ open: true, card, stats, type: 'full' });
  };

  const handlePayPartial = (card: Card, stats: CardStats) => {
    setPaymentModal({ open: true, card, stats, type: 'partial' });
  };

  const handlePaymentConfirm = async (params: CardPaymentParams) => {
    const result = await payCardInvoice(params);
    if (result.success) {
      addToast(result.message);
    }
    return result;
  };

  const getStatsCallback = useCallback(
    (card: Card) => getCardStats(card),
    // getCardStats is now memoized with useCallback in useCards, so this is stable
    [getCardStats]
  );

  const activeCards = cards.filter(c => !c.is_archived);
  const archivedCards = cards.filter(c => c.is_archived);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Meus Cartões</h1>
          <p className="text-sm text-gray-500 mt-0.5">Controle seus cartões de crédito, limites e faturas</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowArchived(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-600 border cursor-pointer whitespace-nowrap transition-colors ${
              showArchived ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}>
            <i className="ri-archive-line" />
            {showArchived ? 'Ocultar arquivados' : 'Ver arquivados'}
            {!showArchived && archivedCards.length > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">{archivedCards.length}</span>
            )}
          </button>
          <button onClick={handleNewCard}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-add-line" />
            Novo Cartão
            {!isPaid && limitCheck.limit !== null && (
              <span className="text-emerald-200 text-xs">({limitCheck.current}/{limitCheck.limit})</span>
            )}
          </button>
        </div>
      </div>

      {!isPaid && limitCheck.limit !== null && (
        <PlanLimitAlert current={limitCheck.current} limit={limitCheck.limit} resource="cartões" onUpgrade={() => setShowUpgrade(true)} />
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1,2,3].map(i => <div key={i} className="h-56 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : activeCards.length === 0 && !showArchived ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
            <i className="ri-bank-card-line text-3xl text-emerald-400" />
          </div>
          <p className="text-base font-700 text-gray-700 mb-1">Nenhum cartão cadastrado</p>
          <p className="text-sm text-gray-400 mb-6 max-w-xs">
            Adicione seus cartões de crédito para controlar limites, faturas e gastos com clareza.
          </p>
          <button onClick={handleNewCard}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" />
            Cadastrar primeiro cartão
          </button>
        </div>
      ) : (
        <>
          {/* Active cards grid */}
          {activeCards.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {activeCards.map(card => (
                <CardGridItem
                  key={card.id}
                  card={card}
                  onView={() => setDetailCard(card)}
                  onEdit={() => handleEdit(card)}
                  onArchive={() => setConfirmAction({ type: 'archive', card })}
                  onDelete={() => handleDeleteRequest(card)}
                />
              ))}
            </div>
          )}

          {/* Archived section */}
          {showArchived && archivedCards.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-sm font-700 text-amber-600">Cartões arquivados</p>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{archivedCards.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {archivedCards.map(card => (
                  <CardGridItem
                    key={card.id}
                    card={card}
                    archived
                    onView={() => setDetailCard(card)}
                    onEdit={() => handleEdit(card)}
                    onArchive={() => setConfirmAction({ type: 'unarchive', card })}
                    onDelete={() => handleDeleteRequest(card)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Toasts — fixed, deve ficar por último para não criar margin-top indevido no header */}
      <div className="fixed top-5 right-5 z-[60] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id}
            className={`flex items-center gap-2.5 px-4 py-3 ${TOAST_BG[t.type]} text-white rounded-xl text-sm font-500 shadow-lg pointer-events-auto`}>
            <i className={`${TOAST_ICON[t.type]} text-base`} />
            {t.message}
          </div>
        ))}
      </div>

      {/* Modals */}
      <CardModal
        open={showModal}
        card={editCard}
        onClose={() => { setShowModal(false); setEditCard(null); }}
        onSave={handleSave}
      />
      <CardDetailModal
        open={!!detailCard}
        card={detailCard}
        onClose={() => setDetailCard(null)}
        onEdit={handleEdit}
        onArchive={c => setConfirmAction({ type: 'archive', card: c })}
        getStats={getStatsCallback}
        onPayFull={handlePayFull}
        onPayPartial={handlePayPartial}
      />
      <CardPaymentModal
        open={paymentModal.open}
        card={paymentModal.card}
        stats={paymentModal.stats}
        initialType={paymentModal.type}
        onClose={() => setPaymentModal(p => ({ ...p, open: false }))}
        onConfirm={handlePaymentConfirm}
      />
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)}
        title="Limite de cartões atingido"
        description={`O plano Free permite apenas ${limitCheck.limit} cartão. Faça upgrade para cartões ilimitados.`}
      />

      {/* Confirm Archive */}
      {confirmAction?.type === 'archive' && (
        <ConfirmDialog
          icon="ri-archive-line" iconBg="bg-amber-50" iconColor="text-amber-500"
          title="Arquivar cartão?"
          description={`O cartão "${confirmAction.card.name}" será ocultado. O histórico será mantido e você pode reativá-lo quando quiser.`}
          confirmLabel="Arquivar" confirmClass="bg-amber-500 hover:bg-amber-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)} onConfirm={handleArchiveConfirm}
        />
      )}
      {confirmAction?.type === 'unarchive' && (
        <ConfirmDialog
          icon="ri-restart-line" iconBg="bg-emerald-50" iconColor="text-emerald-500"
          title="Reativar cartão?"
          description={`O cartão "${confirmAction.card.name}" voltará à listagem principal.`}
          confirmLabel="Reativar" confirmClass="bg-emerald-500 hover:bg-emerald-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)} onConfirm={handleUnarchiveConfirm}
        />
      )}
      {confirmAction?.type === 'deleteBlocked' && (
        <ConfirmDialog
          icon="ri-information-line" iconBg="bg-amber-50" iconColor="text-amber-500"
          title="Cartão com compras vinculadas"
          description={`O cartão "${confirmAction.card.name}" possui compras registradas e não pode ser excluído. Deseja arquivá-lo?`}
          confirmLabel="Arquivar cartão" confirmClass="bg-amber-500 hover:bg-amber-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)}
          onConfirm={() => setConfirmAction({ type: 'archive', card: confirmAction.card })}
        />
      )}
      {confirmAction?.type === 'delete' && (
        <ConfirmDialog
          icon="ri-delete-bin-line" iconBg="bg-red-50" iconColor="text-red-500"
          title="Excluir cartão?" description="Esta ação é irreversível."
          confirmLabel="Excluir" confirmClass="bg-red-500 hover:bg-red-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)}
          onConfirm={() => handleDeleteRequest(confirmAction.card)}
        />
      )}
    </div>
  );
}

/* ─── Card Grid Item ─── */
interface CardGridItemProps {
  card: Card;
  archived?: boolean;
  onView: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

function CardGridItem({ card, archived = false, onView, onEdit, onArchive, onDelete }: CardGridItemProps) {
  const currency = card.currency || 'BRL';
  const digits = card.last_four_digits ? `•••• ${card.last_four_digits}` : '';
  const brand = BRAND_CONFIG[card.brand] ?? BRAND_CONFIG['outro'];
  const today = new Date().getDate();
  const daysToClose = card.closing_day >= today ? card.closing_day - today : 30 - today + card.closing_day;
  const cardTypeInfo = CARD_TYPE_LABELS[card.card_type ?? 'credit'];
  const isDebit = card.card_type === 'debit';

  return (
    <div
      className={`rounded-2xl overflow-hidden border cursor-pointer group transition-all hover:scale-[1.01] ${
        archived ? 'border-amber-100 opacity-75 hover:opacity-100' : 'border-gray-100 hover:border-gray-200'
      }`}
      onClick={onView}
    >
      {/* Card face */}
      <div className="relative overflow-hidden px-5 pt-4 pb-4"
        style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}bb)` }}>
        <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10 bg-white pointer-events-none" style={{ transform: 'translate(30%,-30%)' }} />
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <p className="text-white/60 text-xs">
                {card.bank || 'Cartão'}
                {card.bank_code ? <span className="ml-1 opacity-60">·{card.bank_code}</span> : null}
              </p>
              {/* Type pill */}
              <span className="flex items-center gap-0.5 text-[9px] font-600 px-1.5 py-0.5 rounded-full bg-white/15 text-white/80">
                <i className={`${cardTypeInfo.icon}`} style={{ fontSize: '9px' }} />
                {cardTypeInfo.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-white text-sm font-700">{card.name}</p>
              {archived && <span className="text-xs bg-white/20 text-white px-1.5 py-0.5 rounded-full">Arquivado</span>}
            </div>
          </div>
          <div
            className="relative z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="w-6 h-6 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/25 text-white cursor-pointer"
            >
              <i className="ri-edit-line text-xs" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onArchive(); }}
              className="w-6 h-6 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/25 text-white cursor-pointer"
            >
              <i className={`text-xs ${archived ? 'ri-restart-line' : 'ri-archive-line'}`} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="w-6 h-6 flex items-center justify-center rounded-lg bg-white/10 hover:bg-red-400/40 text-white cursor-pointer"
            >
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div>
            {isDebit ? (
              <>
                <p className="text-white/50 text-xs mb-0.5">Débito direto na conta</p>
                <p className="text-white text-sm font-500 italic opacity-70">Sem limite de crédito</p>
              </>
            ) : (
              <>
                <p className="text-white/50 text-xs mb-0.5">Limite total</p>
                <p className="text-white text-lg font-700">{fmtCurrency(card.limit_amount, currency)}</p>
              </>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            {digits && <p className="text-white/70 text-xs font-mono">{digits}</p>}
            <div className="px-2 py-0.5 rounded-md" style={{ background: brand.bg }}>
              <span className="font-800 tracking-wider" style={{ color: brand.textColor, fontSize: '9px' }}>
                {brand.shortLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info bar */}
      <div className="bg-white px-5 py-3">
        {isDebit ? (
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2.5">
            <span className="flex items-center gap-1 text-sky-600 font-600">
              <i className="ri-bank-card-line" />Cartão de Débito
            </span>
            <span className="text-gray-300">{currency}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2.5">
            <span>Fecha dia <strong className="text-gray-800">{card.closing_day}</strong></span>
            <span>Vence dia <strong className="text-gray-800">{card.due_day}</strong></span>
            <span className={`px-2 py-0.5 rounded-lg font-600 ${
              daysToClose <= 3 ? 'bg-red-50 text-red-600' :
              daysToClose <= 7 ? 'bg-amber-50 text-amber-600' :
              'bg-gray-50 text-gray-500'
            }`}>
              {daysToClose === 0 ? 'Fecha hoje' : `Fecha em ${daysToClose}d`}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />Ver detalhes{isDebit ? '' : ' e fatura'}
          </span>
          <span className="flex items-center gap-1">
            {card.payment_method === 'automatic_debit' && (
              <i className="ri-loop-right-line text-emerald-400" title="Débito automático" />
            )}
            <span className="text-gray-300">{currency}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Confirm Dialog ─── */
interface ConfirmDialogProps {
  icon: string; iconBg: string; iconColor: string;
  title: string; description: string;
  confirmLabel: string; confirmClass: string;
  cancelLabel?: string; loading: boolean;
  onCancel: () => void; onConfirm: () => void;
}
function ConfirmDialog({ icon, iconBg, iconColor, title, description, confirmLabel, confirmClass, cancelLabel = 'Cancelar', loading, onCancel, onConfirm }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 p-6 text-center">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
          <i className={`${icon} ${iconColor} text-xl`} />
        </div>
        <h3 className="text-base font-700 text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-5">{description}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap">
            {cancelLabel}
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 py-2.5 ${confirmClass} text-white font-600 rounded-xl text-sm cursor-pointer disabled:opacity-50 whitespace-nowrap`}>
            {loading ? 'Aguarde...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
